import React, { useEffect } from 'react';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import { useAuth } from '../../hooks/useAuth';
import { analyticsService } from '../../services/upgradeAnalyticsService';

/**
 * FreeTierUpgradePrompt Component
 * Displays a prompt for free-tier employers to upgrade and access AI matching features
 * Tracks analytics for upgrade prompt views and interactions
 */
const FreeTierUpgradePrompt = ({ onUpgradeClick, onDismiss }) => {
  const { user } = useAuth();

  // Track when the prompt is viewed
  useEffect(() => {
    if (user?.id) {
      analyticsService.trackUpgradePromptView(user.id, {
        component: 'FreeTierUpgradePrompt',
        timestamp: new Date().toISOString()
      });
    }
  }, [user?.id]);

  const handleUpgradeClick = () => {
    if (user?.id) {
      analyticsService.trackUpgradeClick(user.id, {
        component: 'FreeTierUpgradePrompt',
        action: 'view_pricing_plans'
      });
    }
    onUpgradeClick();
  };

  const handleDismiss = () => {
    if (user?.id) {
      analyticsService.trackUpgradeDismiss(user.id, {
        component: 'FreeTierUpgradePrompt',
        action: 'back_to_dashboard'
      });
    }
    onDismiss?.();
  };

  return (
    <div className="min-h-[600px] flex flex-col items-center justify-center bg-gradient-to-b from-primary-50 to-blue-50 rounded-lg border border-primary-200 p-8">
      {/* Main Content */}
      <div className="max-w-2xl text-center">
        {/* Icon */}
        <div className="mb-6 flex justify-center">
          <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center">
            <Icon name="Lock" size={40} className="text-primary" />
          </div>
        </div>

        {/* Title */}
        <h2 className="text-3xl font-bold text-text-primary mb-3">
          Unlock AI-Powered Candidate Matching
        </h2>

        {/* Description */}
        <p className="text-lg text-text-secondary mb-6">
          This premium feature is available on paid plans. Upgrade to access intelligent candidate recommendations, smart search filters, market insights, and hiring predictions.
        </p>

        {/* Feature List */}
        <div className="bg-white rounded-lg p-6 mb-8 text-left">
          <h3 className="font-semibold text-text-primary mb-4">Premium Features:</h3>
          <ul className="space-y-3">
            <li className="flex items-start">
              <Icon name="Check" size={20} className="text-green-500 mr-3 flex-shrink-0 mt-0.5" />
              <span className="text-text-secondary">
                <strong>AI Recommendations:</strong> Intelligent candidate matches based on skills, experience, and culture fit
              </span>
            </li>
            <li className="flex items-start">
              <Icon name="Check" size={20} className="text-green-500 mr-3 flex-shrink-0 mt-0.5" />
              <span className="text-text-secondary">
                <strong>Smart Search:</strong> Advanced filtering and ML-powered search capabilities
              </span>
            </li>
            <li className="flex items-start">
              <Icon name="Check" size={20} className="text-green-500 mr-3 flex-shrink-0 mt-0.5" />
              <span className="text-text-secondary">
                <strong>Market Insights:</strong> Real-time data on in-demand skills, salary ranges, and market trends
              </span>
            </li>
            <li className="flex items-start">
              <Icon name="Check" size={20} className="text-green-500 mr-3 flex-shrink-0 mt-0.5" />
              <span className="text-text-secondary">
                <strong>Hiring Predictions:</strong> AI-powered success predictions and hiring analytics
              </span>
            </li>
          </ul>
        </div>

        {/* Pricing Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8 text-left">
          <p className="text-sm text-text-secondary">
            <strong>Pricing:</strong> Available on Pro ($29/mo), Premium ($79/mo), and Agency ($199+/mo) plans.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            variant="primary"
            size="lg"
            onClick={handleUpgradeClick}
            iconName="TrendingUp"
            className="flex-1"
          >
            View Pricing Plans
          </Button>
          {onDismiss && (
            <Button
              variant="outline"
              size="lg"
              onClick={handleDismiss}
              className="flex-1"
            >
              Back to Dashboard
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FreeTierUpgradePrompt;
